/**
 * Photos et documents d'un objet (CDC §8.2, phase 3).
 *
 * Deux blocs, parce que l'usage n'est pas le même : une photo se REGARDE — elle
 * doit s'afficher sans qu'on clique — un document se TÉLÉCHARGE. Le serveur
 * décide lequel est lequel d'après le type réel des octets, jamais d'après
 * l'extension du nom.
 *
 * **Classement.** Les catégories viennent de l'espace, pas du code. On les
 * propose à deux endroits : AVANT l'envoi, pour classer d'un coup une série de
 * photos, et sur chaque document, pour corriger après coup.
 *
 * **Versions.** « Nouvelle version » ne remplace rien : l'ancienne reste
 * consultable dans l'historique (ADR-0009). La liste ne montre que la version
 * en vigueur, sinon une notice révisée trois fois y figurerait trois fois.
 */
import {
  useCallback,
  useEffect,
  useState,
  type ChangeEvent,
  type DragEvent,
  type FormEvent,
} from 'react';

import {
  ApiError,
  api,
  type CategorieDocument,
  type OwnerType,
  type PieceJointe,
} from '../api/client';

/** Ce que le sélecteur de fichiers propose — l'API refait le contrôle. */
const FORMATS = 'image/jpeg,image/png,image/webp,image/heic,image/heif,image/gif,application/pdf';

export function PiecesJointes({ ownerType, ownerId }: { ownerType: OwnerType; ownerId: string }) {
  const [pieces, setPieces] = useState<PieceJointe[]>([]);
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [categories, setCategories] = useState<CategorieDocument[]>([]);
  /** `''` = toutes, `'aucune'` = non classés, sinon l'id d'une catégorie. */
  const [filtre, setFiltre] = useState('');
  const [saisieRecherche, setSaisieRecherche] = useState('');
  const [recherche, setRecherche] = useState('');
  /** Catégorie appliquée aux prochains fichiers ajoutés. */
  const [categorieDepot, setCategorieDepot] = useState('');
  const [historique, setHistorique] = useState<{ nom: string; versions: PieceJointe[] } | null>(
    null,
  );
  const [erreur, setErreur] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(0);
  const [chargement, setChargement] = useState(true);
  const [depotActif, setDepotActif] = useState(false);

  useEffect(() => {
    let actif = true;
    api
      .documentCategories(ownerType)
      .then((c) => {
        if (actif) setCategories(c);
      })
      .catch(() => {
        // Sans catégories, la liste reste pleinement utilisable : on n'affiche
        // simplement pas les sélecteurs de classement.
      });
    return () => {
      actif = false;
    };
  }, [ownerType]);

  const charger = useCallback(async () => {
    try {
      const liste = await api.files(ownerType, ownerId, {
        categoryId: filtre || undefined,
        search: recherche || undefined,
      });
      setPieces(liste);
      setErreur(null);

      // Une URL par image, demandées ensemble. Un échec isolé ne doit pas
      // vider la galerie entière : d'où `allSettled` plutôt que `all`.
      const images = liste.filter((p) => p.estImage);
      const resultats = await Promise.allSettled(images.map((p) => api.fileUrl(p.id)));
      const table: Record<string, string> = {};
      resultats.forEach((r, i) => {
        const piece = images[i];
        if (r.status === 'fulfilled' && piece) {
          table[piece.id] = r.value.url;
        }
      });
      setUrls(table);
    } catch (e) {
      setErreur(message(e, 'Pièces jointes illisibles.'));
    } finally {
      setChargement(false);
    }
  }, [ownerType, ownerId, filtre, recherche]);

  useEffect(() => {
    void charger();
  }, [charger]);

  /**
   * Envoi de nouveaux documents.
   *
   * Un fichier par requête, en série : on obtient une erreur PAR fichier, et
   * une sélection de dix photos ne déclenche pas dix envois simultanés depuis
   * un téléphone en 4G.
   */
  const envoyer = async (fichiers: File[]) => {
    if (fichiers.length === 0) return;

    setErreur(null);
    setInfo(null);
    const echecs: string[] = [];
    for (const fichier of fichiers) {
      setEnCours((n) => n + 1);
      try {
        await api.uploadFile(ownerType, ownerId, fichier, {
          categoryId: categorieDepot || undefined,
        });
      } catch (err) {
        echecs.push(`${fichier.name} : ${message(err, 'envoi impossible')}`);
      } finally {
        setEnCours((n) => n - 1);
      }
    }

    // La liste est rafraîchie AVANT d'annoncer les échecs : `charger()` remet
    // l'erreur à zéro en cas de succès, et effacerait le message.
    await charger();
    if (echecs.length > 0) {
      setErreur(echecs.join(' — '));
    }
  };

  const remplacer = async (piece: PieceJointe, e: ChangeEvent<HTMLInputElement>) => {
    const fichier = e.target.files?.[0];
    // Vidé tout de suite : resélectionner le même fichier après un échec ne
    // déclencherait sinon aucun événement.
    e.target.value = '';
    if (!fichier) return;

    setErreur(null);
    setInfo(null);
    setEnCours((n) => n + 1);
    try {
      const nouvelle = await api.uploadFile(ownerType, ownerId, fichier, {
        replacesId: piece.id,
      });
      await charger();
      // Le serveur rend la version en vigueur quand les octets sont identiques :
      // le dire, plutôt que de laisser croire qu'une version a été créée.
      setInfo(
        nouvelle.id === piece.id
          ? `« ${fichier.name} » est identique à la version en vigueur : aucune version créée.`
          : `Version ${nouvelle.revision} enregistrée. L’ancienne reste consultable dans l’historique.`,
      );
    } catch (err) {
      setErreur(message(err, 'Remplacement impossible.'));
    } finally {
      setEnCours((n) => n - 1);
    }
  };

  const classer = async (piece: PieceJointe, categoryId: string) => {
    setInfo(null);
    try {
      await api.classifyFile(piece.id, categoryId || null);
      await charger();
    } catch (e) {
      setErreur(message(e, 'Classement impossible.'));
    }
  };

  const renommer = async (piece: PieceJointe, label: string | null) => {
    setInfo(null);
    try {
      await api.renameFile(piece.id, label);
      await charger();
    } catch (e) {
      setErreur(message(e, 'Renommage impossible.'));
    }
  };

  const ouvrirHistorique = async (piece: PieceJointe) => {
    try {
      setHistorique({ nom: piece.originalName, versions: await api.fileVersions(piece.id) });
    } catch (e) {
      setErreur(message(e, 'Historique illisible.'));
    }
  };

  const retirer = async (piece: PieceJointe) => {
    const toutes =
      piece.revision > 1 ? `\n\nSes ${piece.revision} versions seront retirées ensemble.` : '';
    if (!window.confirm(`Retirer « ${piece.originalName} » de cette fiche ?${toutes}`)) return;
    try {
      await api.archiveFile(piece.id);
      setHistorique(null);
      await charger();
    } catch (e) {
      setErreur(message(e, 'Retrait impossible.'));
    }
  };

  /** Le document s'ouvre dans un onglet, par une URL fraîchement signée. */
  const ouvrir = async (id: string) => {
    try {
      const { url } = await api.fileUrl(id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setErreur(message(e, 'Ouverture impossible.'));
    }
  };

  const appliquerRecherche = (e: FormEvent) => {
    e.preventDefault();
    setRecherche(saisieRecherche.trim());
  };

  /**
   * Glisser-déposer : plus rapide que le sélecteur de fichiers quand les
   * photos sont déjà visibles (explorateur, galerie du téléphone). Le
   * contrôle réel des octets reste côté serveur, comme pour le sélecteur.
   */
  const surSurvol = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!depotActif) setDepotActif(true);
  };

  const surSortie = (e: DragEvent<HTMLDivElement>) => {
    // `dragleave` se déclenche aussi en passant d'un enfant à un autre : ne
    // désactiver la zone qu'en quittant réellement la carte.
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
      setDepotActif(false);
    }
  };

  const surDepot = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDepotActif(false);
    void envoyer(Array.from(e.dataTransfer.files));
  };

  const filtreActif = filtre !== '' || recherche !== '';
  const photos = pieces.filter((p) => p.estImage);
  const documents = pieces.filter((p) => !p.estImage);

  const actions = (piece: PieceJointe) => (
    <ActionsDocument
      piece={piece}
      categories={categories}
      onClasser={(id) => void classer(piece, id)}
      onRenommer={(label) => void renommer(piece, label)}
      onRemplacer={(e) => void remplacer(piece, e)}
      onHistorique={() => void ouvrirHistorique(piece)}
      onRetirer={() => void retirer(piece)}
    />
  );

  return (
    <div
      className={`carte${depotActif ? ' zone-depot-active' : ''}`}
      onDragOver={surSurvol}
      onDragLeave={surSortie}
      onDrop={surDepot}
    >
      {depotActif && <div className="zone-depot-message">Déposez vos fichiers ici</div>}
      <div className="entete-carte">
        <h2>Photos et documents</h2>
        <div className="actions">
          {categories.length > 0 && (
            <select
              value={categorieDepot}
              onChange={(e) => setCategorieDepot(e.target.value)}
              aria-label="Catégorie des prochains fichiers ajoutés"
              title="Catégorie appliquée aux fichiers que vous allez ajouter"
            >
              <option value="">Ajouter sans catégorie</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  Ajouter en « {c.label} »
                </option>
              ))}
            </select>
          )}
          <label className="bouton-lien discret televerser">
            {enCours > 0 ? `Envoi… (${enCours})` : 'Ajouter'}
            <input
              type="file"
              accept={FORMATS}
              multiple
              hidden
              onChange={(e) => {
                const fichiers = Array.from(e.target.files ?? []);
                e.target.value = '';
                void envoyer(fichiers);
              }}
            />
          </label>
        </div>
      </div>

      {erreur && <div className="message erreur">{erreur}</div>}
      {info && <div className="message succes">{info}</div>}

      {(pieces.length > 0 || filtreActif) && (
        <form className="filtres-documents" onSubmit={appliquerRecherche}>
          {categories.length > 0 && (
            <select
              value={filtre}
              onChange={(e) => setFiltre(e.target.value)}
              aria-label="Filtrer par catégorie"
            >
              <option value="">Toutes les catégories</option>
              <option value="aucune">Non classés</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          )}
          <input
            type="search"
            placeholder="Rechercher un nom de fichier"
            value={saisieRecherche}
            onChange={(e) => setSaisieRecherche(e.target.value)}
            aria-label="Rechercher un document"
          />
          <button className="secondaire discret">Rechercher</button>
          {filtreActif && (
            <button
              type="button"
              className="secondaire discret"
              onClick={() => {
                setFiltre('');
                setSaisieRecherche('');
                setRecherche('');
              }}
            >
              Effacer
            </button>
          )}
        </form>
      )}

      {chargement ? (
        <div className="vide">Chargement…</div>
      ) : pieces.length === 0 ? (
        <div className="vide">
          {filtreActif
            ? 'Aucun document ne correspond à ce filtre.'
            : 'Aucune photo ni document. Glissez-déposez un ou plusieurs fichiers ici, ou ' +
              'utilisez « Ajouter ». Formats acceptés : JPEG, PNG, WebP, HEIC, GIF et PDF.'}
        </div>
      ) : (
        <div className="carte-corps">
          {photos.length > 0 && (
            <div className="galerie">
              {photos.map((p) => (
                <figure key={p.id} className="vignette">
                  {urls[p.id] ? (
                    <a href={urls[p.id]} target="_blank" rel="noopener noreferrer">
                      <img src={urls[p.id]} alt={p.originalName} loading="lazy" />
                    </a>
                  ) : (
                    <div className="vignette-absente">aperçu indisponible</div>
                  )}
                  <figcaption>
                    <span title={p.label ? `Nom d’origine : ${p.originalName}` : p.originalName}>
                      {p.label ?? p.originalName}
                    </span>
                    {p.revision > 1 && <span className="badge-version">v{p.revision}</span>}
                  </figcaption>
                  {actions(p)}
                </figure>
              ))}
            </div>
          )}

          {documents.length > 0 && (
            <ul className="liste-documents">
              {documents.map((d) => (
                <li key={d.id}>
                  <div className="document-principal">
                    <button
                      className="lien-document"
                      title={d.label ? `Nom d’origine : ${d.originalName}` : undefined}
                      onClick={() => void ouvrir(d.id)}
                    >
                      {d.label ?? d.originalName}
                    </button>
                    <span className="aide">
                      {poids(d.byteSize)}
                      {d.revision > 1 && ` · version ${d.revision}`}
                    </span>
                  </div>
                  {actions(d)}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {historique && (
        <div className="historique-versions">
          <div className="entete-carte">
            <strong>Versions de « {historique.nom} »</strong>
            <button className="secondaire discret" onClick={() => setHistorique(null)}>
              Fermer
            </button>
          </div>
          <table>
            <thead>
              <tr>
                <th style={{ width: 150 }}>Version</th>
                <th>Fichier</th>
                <th style={{ width: 110 }}>Déposé le</th>
                <th style={{ width: 80 }}>Taille</th>
                <th style={{ width: 80 }} />
              </tr>
            </thead>
            <tbody>
              {historique.versions.map((v) => (
                <tr key={v.id}>
                  <td>
                    v{v.revision}
                    {!v.superseded && <span className="etat actif"> en vigueur </span>}
                  </td>
                  <td>{v.originalName}</td>
                  <td>{jour(v.createdAt)}</td>
                  <td>{poids(v.byteSize)}</td>
                  <td>
                    <button className="secondaire discret" onClick={() => void ouvrir(v.id)}>
                      Ouvrir
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ActionsDocument({
  piece,
  categories,
  onClasser,
  onRenommer,
  onRemplacer,
  onHistorique,
  onRetirer,
}: {
  piece: PieceJointe;
  categories: readonly CategorieDocument[];
  onClasser: (categoryId: string) => void;
  onRenommer: (label: string | null) => void;
  onRemplacer: (e: ChangeEvent<HTMLInputElement>) => void;
  onHistorique: () => void;
  onRetirer: () => void;
}) {
  // Une catégorie archivée n'est plus proposée, mais un document qui la porte
  // doit continuer à l'afficher : on la réinjecte dans la liste.
  const categorieArchivee =
    piece.categoryId !== null && !categories.some((c) => c.id === piece.categoryId);
  /** `null` = pas en édition ; sinon, le nom en cours de saisie. */
  const [renommage, setRenommage] = useState<string | null>(null);

  if (renommage !== null) {
    return (
      <form
        className="actions-document renommage-document"
        onSubmit={(e) => {
          e.preventDefault();
          onRenommer(renommage.trim() || null);
          setRenommage(null);
        }}
      >
        <input
          type="text"
          value={renommage}
          onChange={(e) => setRenommage(e.target.value)}
          placeholder={piece.originalName}
          autoFocus
          aria-label={`Nouveau nom pour ${piece.label ?? piece.originalName}`}
        />
        <button className="secondaire discret">Enregistrer</button>
        <button type="button" className="secondaire discret" onClick={() => setRenommage(null)}>
          Annuler
        </button>
      </form>
    );
  }

  return (
    <div className="actions-document">
      {(categories.length > 0 || piece.categoryId) && (
        <select
          className="categorie-document"
          value={piece.categoryId ?? ''}
          onChange={(e) => onClasser(e.target.value)}
          aria-label={`Catégorie de ${piece.originalName}`}
        >
          <option value="">Non classé</option>
          {categorieArchivee && piece.categoryId && (
            <option value={piece.categoryId}>{piece.categoryLabel ?? 'Catégorie archivée'}</option>
          )}
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.label}
            </option>
          ))}
        </select>
      )}
      <button
        type="button"
        className="secondaire discret"
        onClick={() => setRenommage(piece.label ?? piece.originalName)}
      >
        Renommer
      </button>
      <label
        className="bouton-lien discret televerser"
        title="Déposer une nouvelle version : l’actuelle reste consultable dans l’historique"
      >
        Nouvelle version
        <input type="file" accept={FORMATS} hidden onChange={onRemplacer} />
      </label>
      {piece.revision > 1 && (
        <button className="secondaire discret" onClick={onHistorique}>
          Historique
        </button>
      )}
      <button className="secondaire discret danger-discret" onClick={onRetirer}>
        Retirer
      </button>
    </div>
  );
}

function message(e: unknown, defaut: string): string {
  return e instanceof ApiError ? e.message : defaut;
}

/** Poids lisible. Les octets bruts ne disent rien à personne. */
function poids(octets: number): string {
  if (octets < 1024) return `${octets} o`;
  if (octets < 1024 * 1024) return `${Math.round(octets / 1024)} Ko`;
  return `${(octets / 1024 / 1024).toFixed(1)} Mo`;
}

function jour(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR');
}
